import * as React from 'react'
import { Button } from '~/sotano-kit/primitives/button'
import { useTabsStore } from '../../../../tabs/store'
import {
  Block,
  CommonBlocksCompoundIndexes,
  ComponentBlock,
  InlineView,
  InstanceBlock,
  ListBlock,
  isRichTextBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { BlockIcon } from '~/components/block-icon'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useThumbnail } from '../use-thumbnail'
import { clsx } from 'clsx'
import {
  buildYDocFromState,
  richTextExtensionsForTransformations,
  stateToBase64,
  yDocToPlainText,
} from '@basehub/tiptap-api-utils'
import { TreeManager } from '@basehub/utils/tree/index'
import { BlockWrapperContextMenu } from '../../../block-wrapper-actions/context-menu'
import { Input } from '~/sotano-kit/primitives/input'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { EditorModeProvider } from '../../../../editor-mode-provider'

export const InlineEntry = ({
  block,
  parent,
  parentPath,
  selectedView,
  className,
  treeManager,
  isRenaming = false,
  tabPrefix = '',
  onFinishedRenaming,
  contextMenu = true,
}: {
  parent: ListBlock
  parentPath: string
  block: InstanceBlock | ComponentBlock
  selectedView: InlineView
  className?: string
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  isRenaming?: boolean
  tabPrefix?: string
  onFinishedRenaming?: () => void
  contextMenu?: boolean
}) => {
  const addTab = useTabsStore((s) => s.addTab)
  const preview = useThumbnail({
    templateId: parent.typeOptions.targetBlockId,
    row: block,
    selectedView,
    treeManager,
  })
  const cardRef = React.useRef<HTMLDivElement>(null)

  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const path = normalizeStringPath({
    path: parentPath + '/' + block.id,
    edgeId: block.id,
    includeEdge: true,
    format: 'string',
  })

  const {
    onChange,
    sync: _,
    valueRef: __,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    customEventsKey: `synced-value:inline-card:${idToSourceBlockId(block)}`,
    value: block.title,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          path,
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: {
            type: block.type,
            title: v,
          },
          strategy: 'merge',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        path,
        block.id,
        block.instanceChildTarget,
        block.type,
        undoManager,
      ]
    ),
  })

  React.useEffect(() => {
    if (!isRenaming) return
    const input = cardRef.current?.querySelector<HTMLInputElement>(
      'input[role="inline-entry-title"]'
    )
    if (!input) return
    setTimeout(() => input.select())
  }, [isRenaming])

  const previewValue = React.useMemo(() => {
    if (preview === '__title') return block.title || 'Untitled'

    if (isRichTextBlock(preview)) {
      const base64 = stateToBase64(
        preview.value.state,
        richTextExtensionsForTransformations
      )
      const yDoc = buildYDocFromState(base64)
      const plainText = yDocToPlainText(
        yDoc,
        richTextExtensionsForTransformations
      )

      if (!plainText) return <span className="italic font-medium">Empty</span>

      return plainText.slice(0, 60) + (plainText.length > 60 ? '...' : '')
    }

    if (preview?.value) return preview.value.toString()

    return <span className="italic font-medium">Empty</span>
  }, [preview, block.title])

  const isTemplate = parent.typeOptions.targetBlockId === block.id

  // without context menu
  if (!contextMenu) {
    return (
      <Button
        unstyled
        focusStyled
        className={clsx(
          isTemplate ? 'text-dark-control-highlight' : 'text-dark-muted',
          'min-w-20 border border-dark-border-solid hover:border-dark-elevated inline-flex items-center gap-x-1 bg-dark-shade hover:bg-dark-shade-hover overflow-hidden text-left cursor-default group rounded-md px-2 py-1 text-sm',
          block.hidden && 'opacity-40',
          className
        )}
        onClick={() => {
          addTab({
            key: `${tabPrefix}${idToSourceBlockId(block)}`,
            partiallyOpen: true,
          })
        }}
      >
        {Boolean(preview === '__title') && (
          <BlockIcon
            block={block}
            iconProps={{
              className: clsx(
                'shrink-0',
                isTemplate ? 'text-dark-control-highlight' : 'text-dark-muted'
              ),
              width: 14,
              height: 14,
            }}
          />
        )}

        <span
          className={clsx(
            'line-clamp-1',
            preview === '__title' && 'font-medium'
          )}
        >
          {previewValue}
        </span>
      </Button>
    )
  }

  return (
    <div
      ref={cardRef}
      className={clsx(
        className,
        block.hidden && 'opacity-40',
        'relative flex flex-col'
      )}
    >
      <Input
        size="xs"
        type="text"
        autoFocus
        disabled={!isRenaming}
        role="inline-entry-title"
        tabIndex={-1}
        className={clsx(
          'absolute inset-0 h-full',
          !isRenaming && 'invisible pointer-events-none select-none'
        )}
        onClick={(e) => {
          e.stopPropagation()
        }}
        placeholder="Untitled"
        onBlur={(e) => {
          e.stopPropagation()
          onFinishedRenaming?.()
        }}
        onKeyDown={(e) => {
          e.stopPropagation()

          if (e.key === 'Escape') {
            e.preventDefault()
            onFinishedRenaming?.()
            e.currentTarget.blur()
          } else if (e.key === 'Enter') {
            onFinishedRenaming?.()
            addTab({
              key: block.id,
              partiallyOpen: true,
            })
            e.currentTarget.blur()
          }
        }}
        onChange={(e) => onChange(e.target.value)}
        {...syncedInputProps}
      />

      <EditorModeProvider block={block} parentBlock={parent}>
        <BlockWrapperContextMenu
          block={block}
          parentBlock={parent}
          path={path}
          canOpenPropertiesPanel={false}
        >
          <Button
            unstyled
            focusStyled
            className={clsx(
              isTemplate ? 'text-dark-control-highlight' : 'text-dark-muted',
              'min-w-20 border border-dark-border-solid hover:border-dark-elevated inline-flex items-center gap-x-1 bg-dark-shade hover:bg-dark-shade-hover overflow-hidden text-left cursor-default group rounded-md px-2 py-1 text-sm'
            )}
            onClick={() => {
              addTab({
                key: `${tabPrefix}${idToSourceBlockId(block)}`,
                partiallyOpen: true,
              })
            }}
          >
            {Boolean(preview === '__title') && (
              <BlockIcon
                block={block}
                iconProps={{
                  className: clsx(
                    'shrink-0',
                    isTemplate
                      ? 'text-dark-control-highlight'
                      : 'text-dark-muted'
                  ),
                  width: 14,
                  height: 14,
                }}
              />
            )}

            <span
              className={clsx(
                'line-clamp-1',
                preview === '__title' && 'font-medium'
              )}
            >
              {previewValue}
            </span>
          </Button>
        </BlockWrapperContextMenu>
      </EditorModeProvider>
    </div>
  )
}
